import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import type { Dispatch, SetStateAction } from 'react'
import { faBuilding, faBuildingFlag, faBusinessTime, faCircleNodes, faDiagramProject, faFileContract, faFileInvoice, faGears, faKey, faLock, faMapLocationDot, faMicrochip, faNetworkWired, faPlugCircleXmark, faReceipt, faRoute, faScrewdriverWrench, faServer, faShieldHalved, faSignsPost, faSquarePlus, faTableColumns, faUser, faUserGroup, faUserTie } from '@fortawesome/free-solid-svg-icons'

import { faBallotCheck, faBarcode, faBells, faBox, faBoxes, faCameraCctv, faChartLine, faClipboardList, faEnvelopeOpenText, faExchange, faHistory, faMagnifyingGlassLocation, faPhoneOffice, faShieldCheck, faSiren, faTrademark, faTrianglePersonDigging, faTruck, faTruckMoving, faWarehouse } from '@fortawesome/pro-solid-svg-icons'
import md5 from 'md5'

import React from 'react'
import { Link } from 'react-router-dom'
import MenuItem from '../../../components/MenuItem'
import { useUser } from '../../../providers/UserProvider'
import { checkPermission } from '../../../util/checkPermission'
import logo from './logo512.png'

import './MenuSidebar.css'

export interface IMenuItem {
  name: string
  permission?: string
  exact: boolean
  path?: string
  icon?: IconDefinition | IconProp
  children?: Array<IMenuItem>
}

export const MENU: IMenuItem[] = [
  {
    name: 'Dashboard',
    icon: faTableColumns,
    path: '/',
    exact: true,
  },
  {
    name: 'BAG',
    exact: false,
    permission: '/frontend/modules/bag',
    icon: faSignsPost,
    children: [
      {
        icon: faMapLocationDot,
        name: 'Alle Adressen',
        exact: false,
        path: '/bag/adressen',
      },
      {
        icon: faMagnifyingGlassLocation,
        name: 'BU Finder',
        exact: false,
        permission: '/frontend/modules/bag/bufinder',
        path: '/bag/bufinder',
      },
      {
        icon: faPlugCircleXmark,
        name: 'Ingetrokken Adressen',
        exact: false,
        permission: '/modules/netwerkbeheer/aansluitingen/revoked-bag',
        path: '/bag/revoked',
      },
    ],
  },
  {
    name: 'Netwerkbeheer',
    exact: false,
    permission: '/frontend/modules/netwerkbeheer',
    icon: faRoute,
    children: [
      {
        name: 'Aansluitingen',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/aansluitingen',
        icon: faBuildingFlag,
        path: '/netwerkbeheer/aansluitingen',
      },
      {
        name: 'Interconnects',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/interconnects',
        icon: faCircleNodes,
        path: '/netwerkbeheer/interconnects',
      },
      {
        name: 'EVC\'s',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/evcs',
        icon: faNetworkWired,
        path: '/netwerkbeheer/evcs',
      },
      {
        name: 'Apparaten',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/apparaten',
        icon: faNetworkWired,
        path: '/netwerkbeheer/apparaten',
      },
      {
        name: 'POP\'s',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/pops',
        icon: faServer,
        path: '/netwerkbeheer/pops',
      },
      {
        name: 'Apparaatmodellen',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/apparaatmodellen',
        icon: faMicrochip,
        path: '/netwerkbeheer/apparaatmodellen',
      },
      {
        name: 'IP Adressen',
        exact: false,
        permission: '/modules/netwerkbeheer/ipadressen',
        icon: faNetworkWired,
        path: '/netwerkbeheer/ip',
      },
      {
        name: 'Workflows',
        exact: false,
        permission: '/modules/connections/workflow/steps',
        icon: faDiagramProject,
        path: '/workflows',
      },
    ],
  },
  {
    name: 'VOIP',
    exact: false,
    permission: '/frontend/modules/voip',
    icon: faPhoneOffice as IconProp,
    children: [
      {
        name: 'Klanten',
        exact: false,
        permission: '/frontend/modules/voip/klanten',
        icon: faBuilding,
        path: '/voip/klanten',
      },
    ],
  },
  {
    name: 'Klanten',
    exact: false,
    permission: '/frontend/modules/klanten',
    icon: faUserTie,
    path: '/customers',
  },
  {
    name: 'Projecten',
    permission: '/frontend/modules/projecten',
    exact: false,
    icon: faDiagramProject,
    children: [
      {
        name: 'Alle Projecten',
        exact: false,
        path: '/projecten',
      },
      {
        name: 'Proposities',
        exact: false,
        icon: faFileContract,
        path: '/propositions',
        permission: '/frontend/modules/projects/propositions',
      },
    ],
  },
  {
    name: 'Provisioning',
    exact: false,
    permission: '/frontend/modules/provisioning',
    icon: faGears,
    children: [
      {
        name: 'Routers',
        exact: false,
        icon: faRoute,
        path: '/provisioning/routers',
      },
    ],
  },
  {
    name: 'RADIUS',
    exact: false,
    permission: '/modules/radius',
    icon: faShieldCheck as IconProp,
    children: [
      {
        name: 'Dashboard',
        exact: false,
        permission: '/modules/radius',
        icon: faChartLine as IconProp,
        path: '/radius/dashboard',
      },
      {
        name: 'User Lookup',
        exact: false,
        permission: '/modules/radius',
        icon: faUser,
        path: '/radius/user-lookup',
      },
      {
        name: 'Active Sessions',
        exact: false,
        permission: '/modules/radius/accounting',
        icon: faCircleNodes,
        path: '/radius/sessions/active',
      },
      {
        name: 'Session History',
        exact: false,
        permission: '/modules/radius/accounting',
        icon: faHistory as IconProp,
        path: '/radius/sessions/history',
      },
      {
        name: 'NAS Management',
        exact: false,
        permission: '/modules/radius/nas',
        icon: faNetworkWired,
        path: '/radius/nas',
      },
      {
        name: 'Authentication Logs',
        exact: false,
        permission: '/modules/radius/postauth',
        icon: faLock,
        path: '/radius/auth-logs',
      },
      {
        name: 'Reports',
        exact: false,
        permission: '/modules/radius',
        icon: faClipboardList as IconProp,
        path: '/radius/reports',
      },
      {
        name: 'Orphaned Records',
        exact: false,
        permission: '/modules/radius/maintenance',
        icon: faScrewdriverWrench,
        path: '/radius/maintenance/orphaned',
      },
    ],
  },
  {
    name: 'Meldingen',
    exact: false,
    permission: '/frontend/modules/meldingen',
    icon: faBells,
    children: [
      {
        name: 'Onderhoud',
        exact: false,
        permission: '/modules/meldingen/onderhoud',
        icon: faTrianglePersonDigging,
        path: '/meldingen/onderhoud',
      },
      {
        name: 'Storingen',
        exact: false,
        permission: '/modules/meldingen/storing',
        icon: faPlugCircleXmark,
        path: '/meldingen/storingen',
      },
      {
        name: 'Cyberdreigingen',
        exact: false,
        permission: '/modules/meldingen/cyberdreigingen',
        icon: faShieldCheck,
        path: '/meldingen/cyberdreigingen',
      },
    ],
  },
  {
    name: 'Monitoring',
    exact: false,
    permission: '/frontend/modules/monitoring',
    icon: faChartLine as IconProp,
    children: [
      {
        name: 'Storingsdashboard',
        exact: false,
        permission: '/frontend/modules/monitoring/issues',
        icon: faSiren as IconProp,
        path: '/monitoring/storingen',
      },
    ],
  },
  {
    name: 'Voorraad',
    exact: false,
    permission: '/frontend/modules/inventory',
    icon: faBoxes, // top-level icon representing inventory
    children: [
      {
        name: 'Warenhuis',
        exact: false,
        permission: '/frontend/modules/inventory/warehouse',
        icon: faWarehouse, // icon representing warehouses/storage
        path: '/voorraad/warenhuizen',
      },
      {
        name: 'Merken',
        exact: false,
        permission: '/frontend/modules/inventory/brands',
        icon: faTrademark, // icon representing brands
        path: '/voorraad/merken',
      },
      {
        name: 'Leveranciers',
        exact: false,
        permission: '/frontend/modules/inventory/vendors',
        icon: faTruck, // icon representing vendors/suppliers
        path: '/voorraad/leveranciers',
      },
      {
        name: 'Producten',
        exact: false,
        permission: '/frontend/modules/inventory/products',
        icon: faBox, // icon representing a product/item
        path: '/voorraad/producten',
      },
      {
        name: 'Stock Dashboards',
        exact: false,
        permission: '/modules/inventory/dashboard',
        icon: faChartLine, // icon representing dashboards/analytics
        path: '/voorraad/dashboards',
      },
      {
        name: 'Inkooporders',
        exact: false,
        permission: '/frontend/modules/inventory/purchaseorder',
        icon: faFileInvoice, // icon representing orders/documents
        path: '/voorraad/inkooporders',
      },
      {
        name: 'Mutaties',
        exact: false,
        permission: '/frontend/modules/inventory/mutations',
        icon: faExchange, // icon representing changes/transactions
        path: '/voorraad/mutaties',
      },
      {
        name: 'Tracking',
        exact: false,
        permission: '/frontend/modules/inventory/tracking',
        icon: faTruckMoving, // icon representing movement/tracking
        path: '/voorraad/tracking',
      },

    ],
  },
  {
    name: 'Administratie',
    exact: false,
    permission: '/frontend/modules/administratie',
    icon: faReceipt,
    children: [
      {
        name: 'Werkbonnen',
        exact: false,
        permission: '/frontend/modules/administratie/werkbonnen',
        icon: faBallotCheck,
        path: '/administratie/werkbonnen',
      },
      {
        name: 'Urenregistratie',
        exact: false,
        permission: '/frontend/modules/administratie/urenregistratie',
        icon: faBusinessTime,
        path: '/administratie/urenregistratie',
      },
    ],
  },
  {
    name: 'Beveiliging',
    exact: false,
    permission: '/frontend/modules/beveiliging',
    icon: faShieldHalved,
    children: [
      {
        name: 'Camerasystemen',
        exact: false,
        permission: '/frontend/modules/beveiliging/camerasystemen',
        icon: faCameraCctv as IconProp,
        path: '/beveiliging/camerasystemen',
      },
      {
        name: 'Alarmsystemen',
        exact: false,
        permission: '/frontend/modules/beveiliging/alarmsystemen',
        icon: faSiren as IconProp,
        path: '/beveiliging/alarmsystemen',
      },
    ],
  },
  {
    name: 'Tools',
    exact: false,
    permission: '/frontend/modules/tools',
    icon: faScrewdriverWrench,
    children: [
      {
        name: 'Belkosten2Exact',
        exact: false,
        permission: '/frontend/modules/tools/belkosten2exact',
        icon: faFileInvoice,
        path: '/tools/belkosten2exact',
      },
      {
        name: 'Gpon Tool',
        exact: false,
        permission: '/frontend/modules/tools/gponpreprocessor',
        icon: faSquarePlus,
        path: '/tools/gponpreprocessor',
      },
      {
        name: 'Zip Check Logs',
        exact: false,
        permission: '/modules/zip-check/logs',
        icon: faFileInvoice,
        path: '/tools/zipchecklogs',
      },
    ],
  },
  {
    name: 'Grondwerk',
    exact: false,
    permission: '/frontend/modules/grondwerk',
    icon: faTrianglePersonDigging,
    path: '/grondwerk',
  },
  {
    name: 'Kaart',
    exact: false,
    permission: '/frontend/modules/kaart',
    icon: faMapLocationDot,
    path: '/kaart',
  },
  {
    name: 'Graafmeldingen',
    exact: false,
    permission: '/frontend/modules/klic/graafmeldingen',
    icon: faMagnifyingGlassLocation,
    path: '/klic/graafmeldingen',
  },
  {
    name: 'Instellingen',
    exact: false,
    permission: '/frontend/modules/instellingen',
    icon: faGears,
    path: '/settings',
    children: [
      {
        name: 'BGT Dataset',
        exact: false,
        permission: '/modules/bgt',
        icon: faTrianglePersonDigging,
        path: '/settings/bgt',
      },
      {
        name: 'KLIC / Kadaster',
        exact: false,
        permission: '/frontend/modules/instellingen/klic',
        icon: faNetworkWired,
        path: '/settings/klic',
      },
      {
        name: 'Zoho CRM',
        exact: false,
        permission: '/frontend/modules/instellingen/zoho',
        icon: faBuilding,
        path: '/settings/zoho',
      },
      {
        name: 'E-mailsjablonen',
        exact: false,
        permission: '/frontend/modules/instellingen/emailsjablonen',
        icon: faEnvelopeOpenText as IconProp,
        path: '/settings/emailsjablonen',
      },
      {
        name: 'Barcode Scanner',
        exact: false,
        permission: '/frontend/modules/instellingen/scanner',
        icon: faBarcode,
        path: '/settings/scanner',
      },
      {
        name: 'Outer VLANs',
        exact: false,
        permission: '/frontend/modules/instellingen/outervlan',
        icon: faNetworkWired,
        path: '/settings/outervlan',
      },
      {
        name: 'Rate-Limit Whitelist',
        exact: false,
        permission: '/modules/systeem/rate-limit-whitelist',
        icon: faShieldCheck,
        path: '/settings/rate-limit-whitelist',
      },
      {
        name: 'Autologin Tokens',
        exact: false,
        permission: '/modules/systeem/kiosk',
        icon: faKey,
        path: '/settings/kiosk',
      },
      {
        name: 'Toegangsbeheer',
        exact: false,
        permission: '/frontend/modules/instellingen/gebruikers',
        icon: faLock,
        path: '/settings/toegang',
        children: [
          {
            name: 'Gebruikers',
            exact: false,
            permission: '/frontend/modules/instellingen/gebruikers',
            icon: faUser,
            path: '/settings/toegang/gebruikers',
          },
          {
            name: 'Groepen',
            exact: false,
            permission: '/frontend/modules/instellingen/groepen',
            icon: faUserGroup,
            path: '/settings/toegang/groepen',
          },
        ],
      },
    ],
  },
]

interface MenuSidebarProps {
  sidebar: {
    get: boolean
    set: Dispatch<SetStateAction<boolean>>
  }
}

function MenuSidebar(props: MenuSidebarProps) {
  const { user, permissions } = useUser()

  // Red accent on development, blue in production, so the two are told apart at a glance.
  const isDev = process.env.NODE_ENV === 'development'
  const sidebarAccent = isDev ? 'sidebar-accent-red' : 'sidebar-accent-blue'

  return (
    <aside className={`app-sidebar bg-body-secondary shadow-lg ${sidebarAccent}`} data-bs-theme="dark">
      <div className="sidebar-brand">
        <Link to="/" className="brand-link">
          <img
            src={logo}
            alt="Datafiber.Network logo"
            className="brand-image rounded-circle shadow opacity-75"
          />
          <span className="brand-text fw-light">Datafiber.Network</span>
        </Link>
      </div>
      <div className="sidebar-wrapper">
        <div className="user-panel mt-2 pb-3 mb-3 d-flex">
          <div className="image">
            <img
              src={`https://www.gravatar.com/avatar/${md5(user.email ? user.email : 'operations@datafiber.nl')}?s=160&d=retro&r=pg`}
              className="rounded-circle shadow-sm"
              alt="User"
            />
          </div>
          <div className="info">
            <Link to="/profile" className="d-block">
              {user.firstName}
              {' '}
              {user.lastName}
            </Link>
          </div>
        </div>
        <nav>
          <ul className="nav sidebar-menu flex-column nav-indent" role="menu">
            {MENU.map((menuItem: IMenuItem) => {
              if (menuItem.permission && checkPermission(menuItem.permission, 'read', permissions) === false) {
                return null
              }

              return (
                <MenuItem key={menuItem.name} menuItem={menuItem} />
              )
            })}
          </ul>
        </nav>
      </div>
    </aside>
  )
}

export default MenuSidebar
